import type { NormalizedPoint } from '../model';
import type { PaperSize } from './geometry';
import type { PaperArrow, RenderableArrow } from './annotationRendering';
import { normalizedToPixel } from './bonds';

export type ArrowHandle = 'from' | 'to' | 'control-0' | 'control-1' | 'move';

// Convert a legacy quadratic to an exactly equivalent cubic when exposing its handles.
export function arrowControls(arrow: RenderableArrow, size: PaperSize): readonly [NormalizedPoint, NormalizedPoint] {
  if (arrow.kind === 'curved-arrow' && arrow.controls) return arrow.controls;
  const a = normalizedToPixel(arrow.from, size);
  const b = normalizedToPixel(arrow.to, size);
  const length = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  const bow = arrow.kind === 'curved-arrow' ? arrow.bow : 0;
  const q = { x: (a.x + b.x) / 2 - ((b.y - a.y) * bow) / length, y: (a.y + b.y) / 2 + ((b.x - a.x) * bow) / length };
  return [a, b].map((p) => ({
    x: (p.x + (2 * (q.x - p.x)) / 3) / size.width,
    y: (p.y + (2 * (q.y - p.y)) / 3) / size.height
  })) as [NormalizedPoint, NormalizedPoint];
}

export function defaultArrowControls(
  from: NormalizedPoint,
  to: NormalizedPoint,
  size: PaperSize
): readonly [NormalizedPoint, NormalizedPoint] {
  const a = normalizedToPixel(from, size);
  const b = normalizedToPixel(to, size);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return [0.05, 0.8].map((along) => ({
    x: (a.x + along * dx + 0.85 * dy) / size.width,
    y: (a.y + along * dy - 0.85 * dx) / size.height
  })) as [NormalizedPoint, NormalizedPoint];
}

export function arrowHandlePoints(
  arrow: RenderableArrow,
  size: PaperSize
): readonly { handle: ArrowHandle; point: NormalizedPoint }[] {
  return [
    { handle: 'from', point: arrow.from },
    { handle: 'to', point: arrow.to },
    ...(arrow.kind === 'curved-arrow'
      ? arrowControls(arrow, size).map((point, index) => ({ handle: `control-${index}` as ArrowHandle, point }))
      : [])
  ];
}

export function editableArrow(arrow: RenderableArrow, size: PaperSize): PaperArrow {
  const common = {
    id: arrow.id,
    from: arrow.fromAnchor ?? { kind: 'point' as const, at: arrow.from },
    to: arrow.toAnchor ?? { kind: 'point' as const, at: arrow.to },
    style: {
      ...(arrow.color ? { color: arrow.color } : {}),
      ...(arrow.lineStyle ? { lineStyle: arrow.lineStyle } : {})
    }
  };
  return arrow.kind === 'curved-arrow'
    ? { ...common, kind: arrow.kind, head: arrow.head, controls: arrowControls(arrow, size) }
    : { ...common, kind: arrow.kind, arrowKind: arrow.arrowKind, ...(arrow.overlay ? { overlay: arrow.overlay } : {}) };
}
